import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, CheckCircle2, Loader2, X, AlertCircle, AlertTriangle, UserCheck, Zap, GitBranch, Flag, ArrowDown, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

// 🎯 أيقونة ولون مميز لكل نوع نود - عشان التايملاين يبان "قصة" واضحة
// (قرار/مهمة بشرية/أكشن تلقائي) مش مجرد سطور نص متشابهة
const NODE_ICONS = {
  task: { Icon: UserCheck, color: 'text-primary', bg: 'bg-primary-soft', ring: 'ring-primary-soft' },
  action: { Icon: Zap, color: 'text-accent', bg: 'bg-accent-soft', ring: 'ring-accent-soft' },
  condition: { Icon: GitBranch, color: 'text-status-warning', bg: 'bg-status-warning-bg', ring: 'ring-status-warning-bg' },
  start: { Icon: PlayCircle, color: 'text-status-success', bg: 'bg-status-success-bg', ring: 'ring-status-success-bg' },
  end: { Icon: Flag, color: 'text-status-neutral', bg: 'bg-status-neutral-bg', ring: 'ring-status-neutral-bg' },
  stop: { Icon: Flag, color: 'text-status-neutral', bg: 'bg-status-neutral-bg', ring: 'ring-status-neutral-bg' },
};
const nodeVisual = (type) => NODE_ICONS[(type || '').toLowerCase()] || NODE_ICONS.task;

const WorkflowHistoryModal = ({ module, refId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontClass = isRTL ? 'font-cairo' : 'font-sans';

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/history/${module}/${refId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error("Failed to load history", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [module, refId]);

  const overdueHours = (dueDate) => {
    if (!dueDate) return null;
    const diffMs = Date.now() - new Date(dueDate).getTime();
    return diffMs > 0 ? Math.round(diffMs / 36000) / 100 : null;
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300 ${fontClass}`}
      dir={isRTL ? 'rtl' : 'ltr'} // 🎯 قلب الواجهة
    >
      <div className="bg-surface-card rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-300">

        {/* Header */}
        <div className="p-5 border-b border-line bg-surface-panel flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-soft rounded-lg">
              <Clock className="text-primary" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black text-ink leading-none">{t('workflowHistory.title')}</h2>
              <p className="text-[10px] text-ink-secondary font-bold mt-1 uppercase tracking-tighter">
                {data?.process_name ? `${data.process_name} · ` : ''}{t('workflowHistory.trackingId')} {refId}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-panel rounded-full transition-all text-ink-secondary hover:text-ink">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-surface-card">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="animate-spin text-primary" size={40} />
              <p className="text-xs font-bold text-ink-secondary uppercase">{t('workflowHistory.fetching')}</p>
            </div>
          ) : (!data?.history?.length && !data?.pending?.length) ? (
            <div className="text-center py-20 relative z-10">
              <p className="text-ink-secondary/60 font-black text-sm uppercase">{t('workflowHistory.noHistory')}</p>
            </div>
          ) : (
            <>
              {data?.is_completed && (
                <div className={`mb-6 p-3 flex items-center gap-2 bg-status-success-bg border border-status-success-bg rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle2 size={16} className="text-status-success shrink-0" />
                  <span className="text-[11px] font-black text-status-success uppercase tracking-widest">{t('workflowHistory.processCompleted')}</span>
                </div>
              )}

              {/* 📜 التايملاين المتصل: خلصت (أخضر) -> الموقف الحالي (برتقالي/أحمر) -> المسار المتوقع بعد كده (رمادي شبحي) */}
              <div className={`space-y-6 relative ${isRTL ? 'mr-3' : 'ml-3'}`}>
                <div className={`absolute top-2 bottom-2 w-[3px] bg-surface-panel rounded-full ${isRTL ? 'right-[13px]' : 'left-[13px]'}`}></div>

                {data?.history?.map((h, i) => {
                  const { Icon } = nodeVisual(h.type);
                  return (
                    <div key={`h-${i}`} className="flex gap-6 relative z-10">
                      <div className="w-7 h-7 rounded-full bg-status-success flex items-center justify-center text-ink-inverse ring-4 ring-surface-card shadow-md shrink-0">
                        <CheckCircle2 size={16} />
                      </div>
                      <div className="flex-1 pb-2 border-b border-line-subtle">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="text-sm font-black text-ink flex items-center gap-1.5">
                            <Icon size={13} className="text-ink-secondary" /> {h.step}
                          </h4>
                          <span className="text-[10px] text-ink-secondary font-bold bg-surface-panel px-2 py-1 rounded border border-line">
                            {new Date(h.date).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="px-2 py-0.5 bg-status-success-bg text-status-success rounded font-black uppercase text-[9px] border border-status-success-bg">
                            {h.action}
                          </span>
                          <span className="text-ink-secondary font-bold">{t('workflowHistory.by')}</span>
                          <span className="text-primary font-black">{h.user}</span>
                        </div>
                        {h.memo && (
                          <div className={`mt-3 text-[11px] text-ink-secondary bg-surface-panel p-3 rounded-xl border-line italic shadow-sm ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
                            "{h.memo}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* 📍 الموقف الحالي - نشط أو اتصعّد */}
                {data?.pending?.map((p, i) => {
                  const { Icon } = nodeVisual(p.type);
                  const overdue = p.is_escalated ? overdueHours(p.due_date) : null;
                  const escalated = p.is_escalated;
                  return (
                    <div key={`p-${i}`} className="flex gap-6 relative z-10">
                      <div className={`w-7 h-7 rounded-full ${escalated ? 'bg-status-danger' : 'bg-status-warning'} flex items-center justify-center text-ink-inverse ring-4 ring-surface-card shadow-md shrink-0 ${escalated ? '' : 'animate-pulse'}`}>
                        {escalated ? <AlertTriangle size={14} /> : <Loader2 size={14} className="animate-spin" />}
                      </div>
                      <div className={`flex-1 p-4 rounded-xl border shadow-sm ${escalated ? 'bg-status-danger-bg/60 border-status-danger-bg' : 'bg-status-warning-bg/60 border-status-warning-bg'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {escalated ? <AlertTriangle size={13} className="text-status-danger" /> : <AlertCircle size={13} className="text-status-warning" />}
                          <h3 className={`text-[10px] font-black uppercase tracking-widest ${escalated ? 'text-status-danger' : 'text-status-warning'}`}>
                            {escalated ? t('workflowHistory.overdueTask') : t('workflowHistory.currentTask')}
                          </h3>
                        </div>
                        <div className="flex justify-between items-center bg-surface-card p-3 rounded-lg border border-line-subtle shadow-inner">
                          <span className="text-sm font-black text-ink flex items-center gap-1.5">
                            <Icon size={13} className="text-ink-secondary" /> {p.step}
                          </span>
                          <div className={`flex flex-col ${isRTL ? 'items-start' : 'items-end'}`}>
                            <span className={`text-[10px] text-ink-inverse px-3 py-1 rounded-full font-black shadow-sm ${escalated ? 'bg-status-danger' : 'bg-status-warning'}`}>
                              {p.assignee}
                            </span>
                            <span className="text-[9px] text-ink-secondary mt-1 font-bold">
                              {escalated
                                ? `${t('workflowHistory.overdueSince')} ${overdue}h`
                                : `${t('workflowHistory.since')} ${new Date(p.assigned_on).toLocaleDateString()}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* 🔮 المسار المتوقع بعد كده - خطوة/فروع شبحية (لسه ماوصلتش) عشان يبان "هيروح فين" مش بس "هو فين دلوقتي" */}
                {data?.next_steps?.length > 0 && (
                  <>
                    <div className="flex justify-center relative z-10">
                      <ArrowDown size={16} className="text-ink-secondary/60" />
                    </div>
                    <div className={`flex gap-3 relative z-10 flex-wrap ${isRTL ? 'mr-[-12px]' : 'ml-[-12px]'}`}>
                      {data.next_steps.map((n, i) => {
                        const { Icon, color, bg, ring } = nodeVisual(n.type);
                        return (
                          <div key={`n-${i}`} className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-line ${bg} ring-1 ${ring} opacity-80`}>
                            {n.branch && (
                              <span className="text-[8px] font-black uppercase text-ink-secondary bg-surface-card px-1.5 py-0.5 rounded border border-line">
                                {n.branch}
                              </span>
                            )}
                            <Icon size={13} className={color} />
                            <span className="text-[11px] font-bold text-ink-secondary">{n.step}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className={`text-[9px] text-ink-secondary/60 font-bold uppercase tracking-widest ${isRTL ? 'text-right mr-1' : 'text-left ml-1'}`}>
                      {t('workflowHistory.nextStep')}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-surface-panel border-t border-line flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-dark text-ink-inverse rounded-lg text-xs font-black hover:bg-primary-hover transition-all shadow-lg active:scale-95 uppercase"
          >
            {t('workflowHistory.closeBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowHistoryModal;
